
import React, { useEffect, useMemo, useState } from "react";
import { leagueService } from "../../services/leagueService";
import { LeagueChallenge, LeagueStanding } from "../../league/types";
import { useGuitarMatch } from "../../context/GuitarMatchContext";
import { HAS_CLOUD_SUPPORT } from "../../services/api";

const RulesModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/80 backdrop-blur-md p-6 animate-fade-in">
        <div className="bg-[#020d11] border border-white/10 rounded-[40px] w-full max-w-2xl p-8 relative shadow-2xl overflow-hidden">
            <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white hover:bg-white/10">✕</button>
            
            <div className="text-center mb-8">
                <h3 className="text-3xl font-black italic text-white uppercase tracking-tighter">Règlement <span className="text-smart-gold">League</span></h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.3em] mt-2">Comment grimper au classement ?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                    <h4 className="text-sm font-black text-white uppercase tracking-widest border-b border-white/10 pb-2">1. Système de Points (LP)</h4>
                    <ul className="space-y-3">
                        <li className="flex justify-between text-xs text-slate-300">
                            <span>Base (Run Valide)</span>
                            <span className="font-bold text-white">+20 LP</span>
                        </li>
                        <li className="flex justify-between text-xs text-slate-300">
                            <span>Performance (Score %)</span>
                            <span className="font-bold text-smart-gold">jusqu'à +80 LP</span>
                        </li>
                        <li className="flex justify-between text-xs text-slate-300">
                            <span>Bonus Intégrité (Confiance IA)</span>
                            <span className="font-bold text-cyan-400">+10 LP</span>
                        </li>
                        <li className="p-3 bg-white/5 rounded-xl text-[10px] text-slate-400 italic mt-2">
                            * Un run "INVALID" (triche, silence, audio corrompu) rapporte 0 LP.
                        </li>
                    </ul>
                </div>

                <div className="space-y-4">
                    <h4 className="text-sm font-black text-white uppercase tracking-widest border-b border-white/10 pb-2">2. Divisions</h4>
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-slate-600"></div>
                            <div className="flex-1 text-xs font-bold text-slate-400">BRONZE</div>
                            <div className="text-[10px] font-mono text-slate-600">0 - 699 LP</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-slate-300"></div>
                            <div className="flex-1 text-xs font-bold text-slate-200">SILVER</div>
                            <div className="text-[10px] font-mono text-slate-500">700 - 1399 LP</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.4)]"></div>
                            <div className="flex-1 text-xs font-bold text-yellow-500">GOLD</div>
                            <div className="text-[10px] font-mono text-yellow-500/60">1400 - 2199 LP</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.4)]"></div>
                            <div className="flex-1 text-xs font-bold text-cyan-400">PLATINUM</div>
                            <div className="text-[10px] font-mono text-cyan-400/60">2200 - 2999 LP</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.4)] animate-pulse"></div>
                            <div className="flex-1 text-xs font-bold text-purple-400">DIAMOND</div>
                            <div className="text-[10px] font-mono text-purple-400/60">3000+ LP</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

export const LeagueHub: React.FC<{
  onSelectChallenge: (c: LeagueChallenge) => void;
}> = ({ onSelectChallenge }) => {
  const { userProfile } = useGuitarMatch();
  const userId = useMemo(() => (userProfile?.pseudo || "user").toLowerCase().replace(/\s+/g, "_"), [userProfile]);

  const [challenges, setChallenges] = useState<LeagueChallenge[]>([]);
  const [standing, setStanding] = useState<LeagueStanding | null>(null);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const season = await leagueService.backend.getSeason();
        const weekly = await leagueService.backend.getWeeklyChallenges(season.id);
        setChallenges(weekly);

        const all = await leagueService.backend.getStandings(season.id);
        const me = all.find(s => s.userId === userId) || null;
        setStanding(me);
      } catch (e) {
        console.warn("League data fetch failed", e);
      }
    })();
  }, [userId]);

  return (
    <div className="space-y-8 animate-fade-in pb-20 relative">
      {showRules && <RulesModal onClose={() => setShowRules(false)} />}

      <div className="bg-gradient-to-br from-[#002A34] to-black border border-white/10 rounded-[40px] p-8 relative overflow-hidden">
        <button 
            onClick={() => setShowRules(true)}
            className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white hover:bg-white hover:text-black transition-all"
            title="Règlement"
        >
            ?
        </button>

        <div className="flex items-end justify-between gap-6 relative z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">League — Saison en cours</div>
            <div className="text-3xl font-black italic text-white mt-2">Ta Division : <span className="text-smart-gold">{standing?.division || "BRONZE"}</span></div>
            <div className="text-slate-400 text-sm mt-2">3 défis officiels / semaine • Run invalide = 0 point</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">LP</div>
            <div className="text-5xl font-black italic text-smart-accent">{standing?.lp ?? 0}</div>
            <div className="text-[10px] font-mono text-slate-500 mt-2">Rank #{standing?.rank ?? "—"}</div>
          </div>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex items-end justify-between px-2">
          <h3 className="text-[12px] font-black text-slate-400 uppercase tracking-[0.4em]">Défis officiels (semaine)</h3>
          <div className="text-[10px] font-mono text-slate-500">
            {HAS_CLOUD_SUPPORT ? "Objectif : run VALID + score élevé" : "Mode Démo (Cloud Requis)"}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {challenges.map(c => (
            <div key={c.id} className={`bg-white/5 border border-white/10 rounded-[32px] p-6 transition-all ${HAS_CLOUD_SUPPORT ? 'hover:border-white/20' : 'opacity-60 grayscale'}`}>
              <div className="flex items-center justify-between gap-6">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-500">Défi officiel</div>
                  <div className="text-2xl font-black italic text-white mt-1">{c.title}</div>
                  <div className="text-slate-400 text-sm mt-2">{c.bpm} BPM • Grille : <span className="font-mono text-slate-300">{c.grid}</span></div>
                </div>
                {HAS_CLOUD_SUPPORT ? (
                    <button
                        onClick={() => onSelectChallenge(c)}
                        className="px-6 py-4 rounded-[24px] bg-smart-gold text-black font-black uppercase tracking-widest hover:bg-white transition-all shadow-lg"
                    >
                        Jouer
                    </button>
                ) : (
                    <div className="px-4 py-2 bg-white/5 rounded-xl text-[8px] font-black text-slate-500 uppercase">
                        Cloud requis
                    </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
